import { useState } from 'react';
import { toast } from 'react-toastify';
import { retrieveUnifiedError } from '@/api/utils';
import { FileType, useAddCaseFilesMutation, useDeleteCaseFileMutation } from '@/queries/file';
import { useUploadFile } from '@/hooks/useUploadFile';
import { mapFilesBeforeUpload } from '@/utils/helpers';

type UseSummaryUploadProps = {
  caseId: string;
};

export const useSummaryFilesUpload = ({ caseId }: UseSummaryUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: deleteFileMutation } = useDeleteCaseFileMutation();
  const { mutateAsync: attachFilesMutation } = useAddCaseFilesMutation();
  const { uploadFiles } = useUploadFile(FileType.Case);

  const deleteFile = async (fileId: string) => {
    try {
      await deleteFileMutation({ caseId, fileId });
      toast.success('File has been deleted successfully');
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(`Error while deleting file. ${error.originalMessage}`);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files?.length) return;
    try {
      setIsUploading(true);
      const tmdxFiles = mapFilesBeforeUpload(Array.from(files));
      const uploadedFiles = await uploadFiles(tmdxFiles);
      await attachFilesMutation({ caseId, files: uploadedFiles });
      toast.success('File has been uploaded successfully.');
    } catch {
      toast.error('Error occured on files uploading');
    } finally {
      setIsUploading(false);
    }
  };

  return { deleteFile, handleFileUpload, isUploading };
};
